import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import { motion } from 'framer-motion';
import ProjectCard from '@/components/ProjectCard';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock Projects Data (will be replaced with API data)
const mockProjects = [
  {
    id: 1,
    name: 'EcoTrack',
    tagline: 'Tracking carbon footprint for local businesses',
    description: 'A platform that helps small businesses measure and reduce their carbon emissions through actionable insights.',
    status: 'Building',
    statusColor: 'bg-pink-500',
    domain: 'Climate Tech',
    team_members: 3
  },
  {
    id: 2,
    name: 'SkillSwap',
    tagline: 'Peer-to-peer learning marketplace for students',
    description: 'Connect with other students to exchange skills — teach what you know, learn what you need.',
    status: 'Testing',
    statusColor: 'bg-blue-500',
    domain: 'EdTech',
    team_members: 3
  },
  {
    id: 3,
    name: 'LocalVoice',
    tagline: 'Amplifying community concerns to local government',
    description: 'A digital platform connecting citizens with local representatives to report issues and track resolutions.',
    status: 'Ideation',
    statusColor: 'bg-yellow-500',
    domain: 'Civic Tech',
    team_members: 1
  },
  {
    id: 4,
    name: 'MediRemind',
    tagline: 'Medication reminders for elderly care',
    description: 'Simple, voice-based medication reminders designed specifically for elderly users.',
    status: 'Live',
    statusColor: 'bg-green-500',
    domain: 'HealthTech',
    team_members: 3
  },
  {
    id: 5,
    name: 'FoodBridge',
    tagline: 'Connecting restaurants with food banks',
    description: 'Platform to reduce food waste by connecting restaurants with surplus food to local shelters and food banks.',
    status: 'Building',
    statusColor: 'bg-pink-500',
    domain: 'Social Impact',
    team_members: 2
  },
  {
    id: 6,
    name: 'StudyBuddy AI',
    tagline: 'AI-powered study companion for exam preparation',
    description: 'Personalized study plans and quiz generation using AI to help students prepare more effectively.',
    status: 'Testing',
    statusColor: 'bg-blue-500',
    domain: 'EdTech',
    team_members: 2
  }
];

const ExploreProjects = () => {
  const { isDark } = useTheme();
  const [projects, setProjects] = useState(mockProjects);
  const [filteredProjects, setFilteredProjects] = useState(mockProjects);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [activeFilter, searchQuery, projects]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Building":
        return "bg-pink-500";
      case "Testing":
        return "bg-blue-500";
      case "Ideation":
        return "bg-yellow-500";
      case "Live":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects`);
      // Map API data to match component expectations
      const mappedProjects = response.data.map(project => ({
        ...project,
        domain: project.category || project.domain,
        statusColor: getStatusColor(project.status),
        team_members: project.team?.length || 0
      }));
      setProjects(mappedProjects);
      setFilteredProjects(mappedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Use mock data if API fails
      setProjects(mockProjects);
      setFilteredProjects(mockProjects);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    // Filter by status
    if (activeFilter !== 'All') {
      filtered = filtered.filter((p) => p.status === activeFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.tagline.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProjects(filtered);
  };

  const filters = ['All', 'Ideation', 'Building', 'Testing', 'Live'];

  return (
    <motion.div 
      className={`pt-20 min-h-screen transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`}
      data-testid="explore-projects-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <section className={`py-16 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-b border-purple-500/20' 
          : 'bg-white shadow-sm border-b-4 border-indigo-600'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className={`inline-block px-6 py-2 rounded-full text-sm font-semibold ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' 
                : 'bg-purple-100 text-purple-700 border border-purple-300'
            }`}>
              Discover Projects
            </span>
          </motion.div>

          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.h1 
              className={`text-4xl sm:text-5xl font-bold mb-4 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}
              data-testid="explore-page-title"
            >
              Explore Real World Projects
            </motion.h1>
          </motion.div>
          <motion.p 
            className={`text-center text-lg mb-8 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Join an active project or get inspired to start your own.
          </motion.p>

          {/* Search and Filters */}
          <motion.div 
            className="flex flex-col md:flex-row gap-4 items-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Search Bar */}
            <motion.div 
              className="relative flex-1 w-full"
              whileFocus={{ scale: 1.02 }}
            >
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} size={20} />
              <input
                type="text"
                placeholder="Search by project name, domain, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-full transition shadow-sm hover:shadow-md ${
                  isDark 
                    ? 'bg-[#2d1b4e] border-purple-500/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                }`}
                data-testid="search-input"
              />
            </motion.div>

            {/* Filters */}
            <div className="flex gap-2 flex-wrap justify-center">
              {filters.map((filter, index) => (
                <motion.button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-5 py-2.5 rounded-full font-semibold transition shadow-sm border-2 ${
                    activeFilter === filter
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg border-purple-700'
                      : isDark
                        ? 'bg-purple-900/30 text-purple-300 border-purple-500/30 hover:bg-purple-900/50'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border-gray-300'
                  }`}
                  data-testid={`filter-${filter.toLowerCase()}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {filter}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Projects Count */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p 
            className={`text-sm font-medium ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {filteredProjects.length} projects found
          </motion.p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <motion.div 
              className={`text-center ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Loading projects...
            </motion.div>
          ) : filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className={`relative rounded-3xl overflow-hidden ${
                    isDark 
                      ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border border-purple-500/20' 
                      : 'bg-white border border-gray-200 shadow-lg'
                  }`}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  {/* Status Badge */}
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white ${project.statusColor} flex items-center gap-1 z-10`}>
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    {project.status}
                  </div>

                  {/* Card Content */}
                  <div className="p-6">
                    {/* Project Icon/Logo Placeholder */}
                    <div className={`w-16 h-16 rounded-2xl mb-4 flex items-center justify-center ${
                      isDark ? 'bg-purple-600/30' : 'bg-purple-100'
                    }`}>
                      <svg className={`w-8 h-8 ${
                        isDark ? 'text-purple-400' : 'text-purple-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>

                    {/* Project Name */}
                    <h3 className={`text-xl font-bold mb-2 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {project.name}
                    </h3>

                    {/* Domain/Category Badge */}
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                      isDark 
                        ? 'bg-purple-600/20 text-purple-300' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {project.domain || project.category}
                    </span>

                    {/* Tagline/Description */}
                    <p className={`text-sm mb-4 line-clamp-2 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {project.tagline || project.description}
                    </p>

                    {/* Team Members */}
                    <div className="flex items-center gap-2 mb-6">
                      <div className="flex -space-x-2">
                        {[...Array(project.team_members || project.team?.length || 0)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                              isDark 
                                ? 'bg-purple-600/30 border-[#1a0b2e]' 
                                : 'bg-purple-100 border-white'
                            }`}
                          >
                            <svg className={`w-4 h-4 ${
                              isDark ? 'text-purple-300' : 'text-purple-600'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        ))}
                      </div>
                      <span className={`text-xs ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {project.team_members || project.team?.length || 0} {(project.team_members || project.team?.length || 0) === 1 ? 'member' : 'members'}
                      </span>
                    </div>

                    {/* View Details Button */}
                    <Link to={`/project/${project.id}`}>
                      <motion.button
                        className="w-full px-4 py-2 rounded-full text-sm font-semibold transition bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        View Details →
                      </motion.button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div 
              className={`text-center py-12 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
              data-testid="no-projects-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              No projects found. Try adjusting your filters or search query.
            </motion.div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className={`py-16 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-t border-purple-500/20' 
          : 'bg-gradient-to-r from-indigo-600 to-purple-600 border-t-4 border-indigo-800'
      }`} data-testid="explore-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className={`rounded-3xl p-8 border-2 ${
              isDark 
                ? 'bg-[#2d1b4e]/50 border-purple-500/30' 
                : 'bg-white/10 border-white/20'
            }`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2 
              className="text-3xl font-bold mb-4 pb-4 text-white"
            >
              Got an idea of your own?
            </motion.h2>
            <motion.p 
              className="text-xl mb-8 text-white/90"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Turn it into the next real-world project at Prodizzy.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/submit-idea"
                  className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl"
                  data-testid="explore-cta-submit-idea"
                >
                  Submit an Idea
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/community"
                  className="inline-block bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-purple-600 transition shadow-lg hover:shadow-xl"
                  data-testid="explore-cta-join-community"
                >
                  Join Our Community
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default ExploreProjects;
